import { useEffect } from 'react';
import { X, Sparkles, AlertTriangle, Bell, CheckCircle2 } from 'lucide-react';
import { cn } from '@/components/ui';
import { useTransitionState } from '@/hooks/useTransitionState';

/*
  NotificationsDrawer — slide-in справа. Использует shared useTransitionState
  для exit-анимации (правило 6 motion principles).

  Fix 2026-05-21:
  - Toggle behavior: повторный клик на bell-кнопку в rail закрывает (см. AppShell)
  - Full off-screen translate: translateX(calc(100% + 24px)) учитывает right-4
    margin, drawer полностью уезжает за viewport
*/

interface Notification {
  id: string;
  type: 'ai' | 'urgent' | 'info' | 'success';
  title: string;
  body: string;
  time: string;
}

const MOCK: Notification[] = [
  {
    id: '1',
    type: 'ai',
    title: 'AI заметил паттерн',
    body: 'host-999 упал 4 раза за месяц, похожие падения у host-1001 — оба у площадки DC-4.',
    time: '14 мин',
  },
  {
    id: '2',
    type: 'urgent',
    title: 'Slack adapter: invalid_auth',
    body: 'Bot token недействителен. Проверь /etc/kaname/.env.',
    time: '32 мин',
  },
  {
    id: '3',
    type: 'success',
    title: 'Модуль "VPS Health" опубликован',
    body: 'Иван Соколов опубликовал новый модуль в Marketplace.',
    time: '1 ч',
  },
  {
    id: '4',
    type: 'info',
    title: 'Новый сотрудник',
    body: 'Мария Волкова присоединилась к демо-компании.',
    time: '3 ч',
  },
];

interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationsDrawer({ open, onClose }: NotificationsDrawerProps) {
  const { phase, shouldRender, isExiting } = useTransitionState(open, {
    enter: 320,
    exit: 280,
  });

  // Click outside → close (только когда полностью open, не во время animation)
  useEffect(() => {
    if (phase !== 'open') return;
    const handler = (e: MouseEvent) => {
      const drawer = document.getElementById('notifications-drawer');
      if (drawer && !drawer.contains(e.target as Node)) {
        // Игнорим клики по bell-кнопке (она сама toggle'ит)
        const bell = (e.target as HTMLElement).closest('[aria-label="Уведомления"]');
        if (!bell) onClose();
      }
    };
    const t = window.setTimeout(() => document.addEventListener('click', handler), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('click', handler);
    };
  }, [phase, onClose]);

  if (!shouldRender) return null;

  return (
    <aside
      id="notifications-drawer"
      role="dialog"
      aria-label="Уведомления"
      className={cn(
        'fixed top-4 right-4 bottom-4 z-drawer flex w-notifications flex-col',
        'rounded-xl border border-line bg-canvas',
        // Layered shadow (правило 7: depth)
        'shadow-notifications',
        isExiting
          ? 'animate-slide-out-right' // ease-in на exit
          : 'animate-slide-in-right', // bouncy enter
      )}
      // CRITICAL: pointer-events отключаются при exit, чтобы drawer не ловил клики
      // пока exit-анимация играет (правило 12 motion principles)
      style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
    >
      <header className="flex items-center justify-between border-b border-line px-5 py-4">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-brand-accent" />
          <h2 className="text-base font-semibold tracking-tight">Уведомления</h2>
          <span className="rounded-xs bg-brand-accent px-1.5 py-0.5 text-2xs leading-none font-semibold text-white">
            {MOCK.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="grid size-8 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-muted hover:text-fg dark:hover:bg-white/10"
        >
          <X size={16} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        <div className="flex flex-col gap-2">
          {MOCK.map((n) => (
            <NotificationItem key={n.id} notif={n} />
          ))}
        </div>
      </div>

      <footer className="border-t border-line px-5 py-3 text-center">
        <button type="button" className="text-xs text-fg-subtle transition-colors hover:text-fg">
          Открыть всё в истории
        </button>
      </footer>
    </aside>
  );
}

function NotificationItem({ notif }: { notif: Notification }) {
  const { icon, ring, fg } = iconForType(notif.type);
  return (
    <article
      className={cn(
        'group/notif relative cursor-pointer rounded-md border border-line bg-card p-3',
        'transition-card-surface duration-normal ease-out',
        // scale-only hover (no translate-y) — нет jitter
        'hover:scale-101 hover:border-line-strong hover:shadow-md',
      )}
    >
      <div className="flex items-start gap-3">
        <span className={cn('mt-0.5 grid size-8 shrink-0 place-items-center rounded-md', ring, fg)}>
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm leading-tight font-semibold text-fg">{notif.title}</h3>
            <span className="shrink-0 text-2xs text-fg-subtle tabular-nums">{notif.time}</span>
          </div>
          <p className="mt-1 text-notification-body leading-snug text-fg-muted">{notif.body}</p>
        </div>
      </div>
    </article>
  );
}

function iconForType(type: Notification['type']) {
  switch (type) {
    case 'ai':
      return {
        icon: <Sparkles size={16} />,
        ring: 'bg-ai-accent/10',
        fg: 'text-ai-icon dark:text-ai-icon-night',
      };
    case 'urgent':
      return {
        icon: <AlertTriangle size={16} />,
        ring: 'bg-notice-error-accent/10',
        fg: 'text-notification-error-fg dark:text-notification-error-highlight',
      };
    case 'success':
      return {
        icon: <CheckCircle2 size={16} />,
        ring: 'bg-service-enabled-accent/10',
        fg: 'text-service-enabled-text dark:text-service-enabled-highlight',
      };
    case 'info':
    default:
      return {
        icon: <Bell size={16} />,
        ring: 'bg-notification-info-accent/10',
        fg: 'text-notification-info-fg dark:text-notification-info-highlight',
      };
  }
}
